import React from 'react';
import { Route, Routes as RouterRoutes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isAppLang, getEnv } from 'utils';
import Layout from 'components/layout/Layout';
import ProtectedPage from './ProtectedPage';
import PublicPage from './PublicPage';
import NavigateToLanding from './NavigateToLanding';
import {
  Dashboard,
  AdminTicket,
  AdminTicketCategoryList,
  AdminTicketList,
  AlarmEdit,
  AlarmAdd,
  AlarmList,
  BulletinArchiveList,
  BulletinContent,
  BulletinList,
  AutoBulletinContentList,
  BulletinSettings,
  ChangeLog,
  AutoBulletin,
  SingleAutoBulletin,
  ChangePassword,
  Comparison,
  ComparisonAdd,
  ComparisonSourcesAdd,
  ComparisonSubjectsAdd,
  ComparisonViewSource,
  ComparisonViewSubject,
  Content,
  ContentProfile,
  Kiosk,
  Login,
  Notifications,
  OnboardingContainer,
  Pdf,
  Register,
  Resource,
  ResourceBlackListedSet,
  ResourceCategoryManage,
  ResourceProfile,
  ResourceVipAdd,
  ResourceVipAdminPermission,
  ResourceVipList,
  Resources,
  Search,
  SiteSettings,
  StatisticsSubject,
  Subject,
  SubjectAdd,
  SubjectEdit,
  Support,
  TagList,
  UserTicket,
  UserTicketList,
  UsersList,
  Error404,
  Error403,
  ForgotPassword,
  Story,
  ShowTranslations,
  AutoBulletinAdd,
  AutoBulletinEdit,
  TinyUrl,
  AddCompany,
  EditCompany,
  CompanyList,
  Academy,
  AcademyCourse,
  AddUser,
  EditUser,
  BrandBaan,
  BrandBuilder,
  IndustryBuilder,
  BrandbaanAllStories,
  WebBulletin,
  Ecommerce,
  EcommerceReviewsTable,
} from './pages';
import ChatbotLogout from './ChatbotLogout';
import ChatbotOnlyRedirect from './ChatbotOnlyRedirect';

const routePathsToOnePage = ({ paths, page: element }) => {
  return paths.map((path, index) => (
    <Route key={index} path={path} element={element} />
  ));
};

function Routes() {
  const hasFreeSearchPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.FrontEnd?.freeSearch
        ?.permission
  );
  const hasAlarmEditingPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.Alarm?.update
        ?.permission
  );
  const hasAlarmAddingPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.Alarm?.store
        ?.permission
  );
  const hasAlarmListPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.Alarm?.index
        ?.permission
  );
  const hasTicketAdminPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.FrontEnd
        ?.ticketManagement?.permission
  );
  const hasTicketDetailsPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.Ticket?.show
        ?.permission
  );
  const hasTicketListPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.Ticket?.index
        ?.permission
  );
  const hasAdminUserListPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.Monitoring
        ?.getAllUsers?.permission
  );
  const hasUserListPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.User?.index
        ?.permission
  );
  const hasUserAddPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.User?.store
        ?.permission
  );
  const hasUserEditPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.User?.update
        ?.permission
  );
  const hasInstagramStoriesPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.SearchPlatforms
        ?.instagramstories?.permission
  );
  const hasCreateAutoBulletinPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.Variables
        ?.smart_bulletin_count?.int_value_1
  );
  const hasTinyUrlPagePermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.FrontEnd
        ?.shortUrlMenu?.permission
  );
  const hasCompanyAddPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.CompanyInfoAdmin
        ?.store?.permission
  );
  const hasCompanyEditPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.CompanyInfoAdmin
        ?.update?.permission
  );
  const hasCompanyListPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.CompanyInfoAdmin
        ?.index?.permission
  );
  const hasDashboardPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.Dashboard?.index
        ?.permission
  );
  const hasDashboardMenuPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.FrontEnd
        ?.wallDashboardMenu?.permission
  );

  const hasBrandBaanPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.Chart?.index
        ?.permission
  );
  const hasEcommercePermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.SearchPlatforms?.news
        ?.permission
  );

  const userEmail = useSelector(
    (state) => state.accountReducer.currentUser?.user?.email
  );

  const hasBrandBuilderPermission =
    userEmail === getEnv('REACT_APP_BRAND_BUILDER_ACCOUNT');

  const departmentId = useSelector(
    (state) => state.accountReducer.currentUser?.user?.department_id
  );

  const hasChatbotOnlyPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.Variables
        ?.chatbot_only?.permission
  );

  const defaultLandingPageSelectBox = useSelector(
    (state) => state.settingsReducer.filters.public.defaultLandingPageSelectBox
  );

  const isRootUser = () => departmentId === 1;

  if (hasChatbotOnlyPermission) {
    return (
      <RouterRoutes>
        <Route path='/api/auth/logout' element={<ChatbotLogout />} />
        <Route path='/login' element={<PublicPage component={<Login />} />} />
        <Route path='/new' element={<PublicPage component={<Register />} />} />
        <Route
          path='/forgot-password'
          element={<PublicPage component={<ForgotPassword />} />}
        />
        <Route
          path='/change-password'
          element={<ProtectedPage component={<ChangePassword />} />}
        />
        <Route path='*' element={<ChatbotOnlyRedirect />} />
      </RouterRoutes>
    );
  }

  return (
    <RouterRoutes>
      <Route path='/api/auth/logout' element={<ChatbotLogout />} />

      <Route path='/login' element={<PublicPage component={<Login />} />} />
      <Route path='/new' element={<PublicPage component={<Register />} />} />
      <Route
        path='/forgot-password'
        element={<PublicPage component={<ForgotPassword />} />}
      />
      <Route
        path='/change-password'
        element={<ProtectedPage component={<ChangePassword />} />}
      />

      {defaultLandingPageSelectBox.selectedValue === 'chatbot' && (
        <Route path='/' element={<ChatbotOnlyRedirect />} />
      )}

      {routePathsToOnePage({
        paths: ['a/:hash', 'a/:hash/:platform'],
        page: <AutoBulletin />,
      })}

      {routePathsToOnePage({
        paths: ['wb/:hashAndId', 'wb/:hashAndId/:platform'],
        page: <WebBulletin />,
      })}

      {routePathsToOnePage({
        paths: ['s/:hashAndId', 's/:hashAndId/:platform'],
        page: <SingleAutoBulletin />,
      })}

      <Route
        path='/onboarding'
        element={<ProtectedPage component={<OnboardingContainer />} />}
      />
      <Route
        path='/bulletin/:id/pdf/:bulletinTitle'
        element={
          <ProtectedPage component={<Pdf />} hasPermission={!isAppLang('ar')} />
        }
      />
      <Route
        path='/changelog'
        element={<ProtectedPage component={<ChangeLog />} />}
      />

      <Route element={<Layout />}>
        <Route
          path='/'
          element={<ProtectedPage component={<NavigateToLanding />} />}
        />
        {routePathsToOnePage({
          paths: ['/resources', '/resources/:platform'],
          page: <ProtectedPage component={<Resource />} />,
        })}
        {routePathsToOnePage({
          paths: [
            '/contents',
            'contents/:platform',
            '/tags/contents',
            '/tags/contents/:platform',
            '/subjects/contents',
            '/subjects/contents/:platform',
            '/resource-categories/contents',
            '/resource-categories/contents/:platform',
            '/resource-categories/vip/:platform/contents',
          ],
          page: <ProtectedPage component={<Content />} />,
        })}
        {routePathsToOnePage({
          paths: [
            '/contents/:platform/:id',
            '/contents/:platform/:id/:type',
            '/tags/contents/:platform/:id',
            '/tags/contents/:platform/:id/:type',
            '/subjects/contents/:platform/:id',
            '/subjects/contents/:platform/:id/:type',
            '/resource-categories/contents/:platform/:id',
            '/resource-categories/contents/:platform/:id/:type',
            '/resource-categories/vip/:platform/contents/:id',
          ],
          page: <ProtectedPage component={<ContentProfile />} />,
        })}
        {routePathsToOnePage({
          paths: [
            '/statistics',
            '/statistics/:platform',
            'tags/statistics',
            'tags/statistics/:platform',
            '/subjects/statistics',
            '/subjects/statistics/:platform',
            '/resource-categories/statistics',
            '/resource-categories/statistics/:platform',
            '/resource-categories/vip/:platform/statistics',
          ],
          page: <ProtectedPage component={<StatisticsSubject />} />,
        })}
        <Route
          path='/dashboard'
          element={
            <ProtectedPage
              component={<Dashboard />}
              hasPermission={
                hasDashboardPermission && hasDashboardMenuPermission
              }
            />
          }
        />
        <Route
          path='/search'
          element={
            <ProtectedPage
              component={<Search />}
              hasPermission={hasFreeSearchPermission && !isAppLang('ar')}
            />
          }
        />
        <Route
          path='/bulletins/smart/edit/:id'
          element={<ProtectedPage component={<AutoBulletinEdit />} />}
        />
        {routePathsToOnePage({
          paths: ['/brandbaan', '/brandbaan/:platform'],
          page: (
            <ProtectedPage
              hasPermission={hasBrandBaanPermission}
              component={<BrandBaan />}
            />
          ),
        })}
        <Route
          path='/brandbaan/stories'
          element={
            <ProtectedPage
              hasPermission={hasBrandBaanPermission}
              component={<BrandbaanAllStories />}
            />
          }
        />
        <Route
          path='/bulletins/smart/add'
          element={
            <ProtectedPage
              component={<AutoBulletinAdd />}
              hasPermission={hasCreateAutoBulletinPermission}
            />
          }
        />
        {routePathsToOnePage({
          paths: ['/bulletins'],
          page: (
            <ProtectedPage
              component={<BulletinList />}
              hasPermission={!isAppLang('ar')}
            />
          ),
        })}
        {routePathsToOnePage({
          paths: ['/bulletins/smart/:id/:title'],
          page: (
            <ProtectedPage
              component={<AutoBulletinContentList />}
              hasPermission={!isAppLang('ar')}
            />
          ),
        })}
        {routePathsToOnePage({
          paths: ['/bulletins/:id', '/bulletins/:id/:platform'],
          page: (
            <ProtectedPage
              component={<BulletinContent />}
              hasPermission={!isAppLang('ar')}
            />
          ),
        })}
        <Route
          path='/bulletin-archive'
          element={
            <ProtectedPage
              component={<BulletinArchiveList />}
              hasPermission={!isAppLang('ar')}
            />
          }
        />
        <Route
          path='/bulletin-settings'
          element={
            <ProtectedPage
              component={<BulletinSettings />}
              hasPermission={!isAppLang('ar')}
            />
          }
        />
        <Route
          path='/ecommerce/reviews-table'
          element={
            <ProtectedPage
              component={<EcommerceReviewsTable />}
              hasPermission={hasEcommercePermission}
            />
          }
        />
        <Route
          path='/ecommerce/:platform'
          element={
            <ProtectedPage
              component={<Ecommerce />}
              hasPermission={hasEcommercePermission}
            />
          }
        />
        <Route
          path='/kiosk'
          element={
            <ProtectedPage
              component={<Kiosk />}
              hasPermission={!isAppLang('ar')}
            />
          }
        />
        <Route
          path='/settings'
          element={<ProtectedPage component={<SiteSettings />} />}
        />
        <Route
          path='/tags'
          element={<ProtectedPage component={<TagList />} />}
        />
        <Route
          path='/notifications'
          element={<ProtectedPage component={<Notifications />} />}
        />
        <Route
          path='/resource-categories'
          element={<ProtectedPage component={<Resources />} />}
        />
        <Route
          path='/resource-categories/add'
          element={<ProtectedPage component={<ResourceCategoryManage />} />}
        />
        <Route
          path='/resource-categories/edit/:id'
          element={<ProtectedPage component={<ResourceCategoryManage />} />}
        />
        {routePathsToOnePage({
          paths: [
            '/resource-categories/blacklist',
            '/resource-categories/blacklist/:platform',
          ],
          page: <ProtectedPage component={<ResourceBlackListedSet />} />,
        })}
        {routePathsToOnePage({
          paths: [
            '/resource-categories/vip',
            '/resource-categories/vip/:platform',
          ],
          page: <ProtectedPage component={<ResourceVipList />} />,
        })}
        <Route
          path='/resource-categories/vip/add'
          element={<ProtectedPage component={<ResourceVipAdd />} />}
        />
        <Route
          path='/resources/vip/:id/edit'
          element={<ProtectedPage component={<ResourceVipAdd />} />}
        />
        <Route
          path='/resources/:platform/:id'
          element={<ProtectedPage component={<ResourceProfile />} />}
        />
        <Route
          path='/subjects'
          element={<ProtectedPage component={<Subject />} />}
        />
        <Route
          path='/subjects/edit/:id'
          element={<ProtectedPage component={<SubjectEdit />} />}
        />
        <Route
          path='/subjects/add'
          element={<ProtectedPage component={<SubjectAdd />} />}
        />
        <Route
          path='/alarms/edit/:id'
          element={
            <ProtectedPage
              component={<AlarmEdit />}
              hasPermission={hasAlarmEditingPermission}
            />
          }
        />
        <Route
          path='/alarms/add'
          element={
            <ProtectedPage
              component={<AlarmAdd />}
              hasPermission={hasAlarmAddingPermission}
            />
          }
        />
        <Route
          path='/alarms'
          element={
            <ProtectedPage
              component={<AlarmList />}
              hasPermission={hasAlarmListPermission}
            />
          }
        />
        <Route
          path='/support/tickets/:id'
          element={
            <ProtectedPage
              component={<UserTicket />}
              hasPermission={
                !hasTicketAdminPermission && hasTicketDetailsPermission
              }
            />
          }
        />
        <Route
          path='/support/tickets'
          element={
            <ProtectedPage
              component={<UserTicketList />}
              hasPermission={
                !hasTicketAdminPermission && hasTicketListPermission
              }
            />
          }
        />
        <Route
          path='/support'
          element={
            <ProtectedPage
              component={<Support />}
              hasPermission={hasTicketListPermission}
            />
          }
        />
        <Route
          path='/vip-resources-management'
          element={<ProtectedPage component={<ResourceVipAdminPermission />} />}
        />
        <Route
          path='/comparisons'
          element={<ProtectedPage component={<Comparison />} />}
        />
        <Route
          path='/comparisons/add/subject'
          element={<ProtectedPage component={<ComparisonSubjectsAdd />} />}
        />
        <Route
          path='/comparisons/edit-subject/:id/'
          element={<ProtectedPage component={<ComparisonSubjectsAdd />} />}
        />
        <Route
          path='/comparisons/subject/:id/'
          element={<ProtectedPage component={<ComparisonViewSubject />} />}
        />
        <Route
          path='/comparisons/add/source'
          element={<ProtectedPage component={<ComparisonSourcesAdd />} />}
        />
        <Route
          path='/comparisons/edit-source/:id/'
          element={<ProtectedPage component={<ComparisonSourcesAdd />} />}
        />
        <Route
          path='/comparisons/source/:id/:platform'
          element={<ProtectedPage component={<ComparisonViewSource />} />}
        />
        <Route
          path='/comparisons/add'
          element={<ProtectedPage component={<ComparisonAdd />} />}
        />
        <Route
          path='/users'
          element={
            <ProtectedPage
              component={<UsersList isAdmin={true} />}
              hasPermission={hasAdminUserListPermission}
            />
          }
        />
        <Route
          path='/users-management'
          element={
            <ProtectedPage
              component={<UsersList />}
              hasPermission={hasUserListPermission && !isRootUser()}
            />
          }
        />
        <Route
          path='/users/add'
          element={
            <ProtectedPage
              component={<AddUser />}
              hasPermission={hasUserAddPermission}
            />
          }
        />
        <Route
          path='/users/edit/:userId'
          element={
            <ProtectedPage
              component={<EditUser />}
              hasPermission={hasUserEditPermission}
            />
          }
        />
        <Route
          path='/tickets/:id'
          element={
            <ProtectedPage
              component={<AdminTicket />}
              hasPermission={hasTicketAdminPermission}
            />
          }
        />
        <Route
          path='/tickets'
          element={
            <ProtectedPage
              component={<AdminTicketList />}
              hasPermission={hasTicketAdminPermission}
            />
          }
        />
        <Route
          path='/ticket-categories'
          element={
            <ProtectedPage
              component={<AdminTicketCategoryList />}
              hasPermission={hasTicketAdminPermission}
            />
          }
        />
        {routePathsToOnePage({
          paths: [
            '/contents/instagram/stories',
            '/tags/contents/instagram/stories',
            '/subjects/contents/instagram/stories',
            '/resource-categories/contents/instagram/stories',
          ],
          page: (
            <ProtectedPage
              component={<Story />}
              hasPermission={hasInstagramStoriesPermission}
            />
          ),
        })}
        <Route
          path='/langs'
          element={
            <ProtectedPage
              component={<ShowTranslations />}
              hasPermission={getEnv('REACT_APP_ENVIRONMENT') === 'development'}
            />
          }
        />
        <Route
          path='/tiny-url'
          element={
            <ProtectedPage
              component={<TinyUrl />}
              hasPermission={hasTinyUrlPagePermission}
            />
          }
        />
        <Route
          path='/companies'
          element={
            <ProtectedPage
              component={<CompanyList />}
              hasPermission={hasCompanyListPermission}
            />
          }
        />
        <Route
          path='/companies/add'
          element={
            <ProtectedPage
              component={<AddCompany />}
              hasPermission={hasCompanyAddPermission}
            />
          }
        />
        <Route
          path='/companies/edit/:companyId'
          element={
            <ProtectedPage
              component={<EditCompany />}
              hasPermission={hasCompanyEditPermission}
            />
          }
        />
        <Route
          path='/brandBuilder'
          element={
            <ProtectedPage
              component={<BrandBuilder />}
              hasPermission={hasBrandBuilderPermission}
            />
          }
        />
        <Route
          path='/industryBuilder'
          element={
            <ProtectedPage
              component={<IndustryBuilder />}
              hasPermission={hasBrandBuilderPermission}
            />
          }
        />
        <Route
          path='/academy'
          element={<ProtectedPage component={<Academy />} />}
        />
        <Route
          path='/academy/:courseId'
          element={<ProtectedPage component={<AcademyCourse />} />}
        />
        <Route path='/unauthorized' element={<Error403 />} />
      </Route>
      <Route path='*' element={<Error404 />} />
    </RouterRoutes>
  );
}

export default Routes;
